import React, { useEffect } from 'react'
import Style from './createConnector.module.css'
import FeatuerImage from '../../Modules/NewConnector/Versions/Featuers/FeatuerImage'
import { useGetConnectorFeaturesQuery, useGetConnectorVersionsQuery } from '../../../features/marketplace/marketplaceApi'
import { skipToken } from '@reduxjs/toolkit/query/react'

const EMPTY = []
const unique = (values) => [...new Set(values.filter((value) => value !== undefined && value !== null && value !== ''))]

const Field = ({ id, label, value, options, placeholder, disabled, onChange }) => (
    <div className={Style.field}>
        <label htmlFor={id} className={Style.label}>{label}</label>
        <select
            id={id}
            className={`form-control ${Style.select}`}
            value={value || ''}
            disabled={disabled}
            onChange={(event) => onChange(event.target.value || null)}
        >
            <option value="">{placeholder}</option>
            {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
    </div>
)

const useSingleOption = (options, value, onChange) => {
    useEffect(() => {
        if (!value && options.length === 1) onChange(options[0].value)
    }, [options, value, onChange])
}

const PlanStep = ({ integration, plan, onChange }) => {
    const { data: versions = EMPTY, isLoading: isLoadingVersions, isError: versionsError } = useGetConnectorVersionsQuery(integration.id)
    const { data: productTypes = EMPTY, isFetching: isLoadingFeatures } = useGetConnectorFeaturesQuery(
        plan.sourceVersion && plan.targetVersion
            ? { connectorId: integration.id, sourceVersion: plan.sourceVersion, targetVersion: plan.targetVersion }
            : skipToken
    )

    const sourceOptions = unique(versions.map((item) => item.sourceVersion)).map((version) => ({ value: version, label: version }))
    const targetOptions = unique(versions.filter((item) => item.sourceVersion === plan.sourceVersion).map((item) => item.targetVersion))
        .map((version) => ({ value: version, label: version }))
    const typeOptions = productTypes
        .filter((item) => item.latestVersion)
        .map((item) => ({ value: String(item.latestVersion.id), label: item.displayName }))
    const chosenType = productTypes.find((item) => item.latestVersion && String(item.latestVersion.id) === plan.productVersionId)
    const features = chosenType ? chosenType.latestVersion.versionEntities || EMPTY : null

    const setSource = (value) => onChange({ sourceVersion: value, targetVersion: null, productVersionId: null })
    const setTarget = (value) => onChange({ targetVersion: value, productVersionId: null })
    const setType = (value) => onChange({ productVersionId: value })

    useSingleOption(sourceOptions, plan.sourceVersion, setSource)
    useSingleOption(targetOptions, plan.targetVersion, setTarget)
    useSingleOption(typeOptions, plan.productVersionId, setType)

    return (
        <div className={Style.split}>
            <section className={Style.section} aria-labelledby="plan-versions">
                <h3 id="plan-versions" className={Style.sectionTitle}>Versions and product type</h3>
                {versionsError ? (
                    <p className={Style.error} role="alert">The versions could not be loaded.</p>
                ) : (
                    <div className={Style.fields}>
                        <Field
                            id="source-version"
                            label={`${integration.source.shortName} version`}
                            value={plan.sourceVersion}
                            options={sourceOptions}
                            placeholder={isLoadingVersions ? 'Loading...' : 'Select version'}
                            disabled={isLoadingVersions || sourceOptions.length === 0}
                            onChange={setSource}
                        />
                        <Field
                            id="target-version"
                            label={`${integration.target.shortName} version`}
                            value={plan.targetVersion}
                            options={targetOptions}
                            placeholder="Select version"
                            disabled={!plan.sourceVersion}
                            onChange={setTarget}
                        />
                        <Field
                            id="product-type"
                            label="Product type"
                            value={plan.productVersionId}
                            options={typeOptions}
                            placeholder={isLoadingFeatures ? 'Loading...' : 'Select product type'}
                            disabled={!plan.targetVersion || isLoadingFeatures || typeOptions.length === 0}
                            onChange={setType}
                        />
                    </div>
                )}
            </section>

            <section className={Style.section} aria-labelledby="plan-features">
                <h3 id="plan-features" className={Style.sectionTitle}>
                    Included features
                    {features && features.length > 0 ? <span className={Style.count}>{features.length}</span> : null}
                </h3>
                {features === null ? (
                    <p className={Style.muted}>Select versions and a product type to see the included features.</p>
                ) : features.length === 0 ? (
                    <p className={Style.muted}>No features are listed for this product type.</p>
                ) : (
                    <ul className={Style.features}>
                        {features.map((feature) => (
                            <li key={feature.displayName} className={Style.feature}>
                                <span className={Style.featureIcon}><FeatuerImage displayName={feature.displayName} /></span>
                                {feature.displayName}
                            </li>
                        ))}
                    </ul>
                )}
            </section>
        </div>
    )
}

export default PlanStep
